/* Deterministic maths and portfolio statistics. Nothing here calls Math.random,
   so a given seed always replays the same competition. */

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Box-Muller transform, driven by an injected uniform source. */
export function gaussian(rand: () => number): number {
  let u = 0
  let v = 0
  while (u === 0) u = rand()
  while (v === 0) v = rand()
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v)
}

export const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n))

export function pick<T>(rand: () => number, xs: readonly T[]): T {
  return xs[Math.floor(rand() * xs.length) % xs.length]
}

export function weightedPick<T>(rand: () => number, xs: readonly T[], weight: (x: T) => number): T {
  const total = xs.reduce((s, x) => s + weight(x), 0)
  let r = rand() * total
  for (const x of xs) {
    r -= weight(x)
    if (r <= 0) return x
  }
  return xs[xs.length - 1]
}

/** Annualised Sharpe from equity samples of uniform spacing. */
export function sharpe(equity: readonly number[], periodsPerYear = 8760): number {
  if (equity.length < 3) return 0
  const rets: number[] = []
  for (let i = 1; i < equity.length; i++) {
    const prev = equity[i - 1]
    if (prev > 0) rets.push(equity[i] / prev - 1)
  }
  if (rets.length < 2) return 0
  const mean = rets.reduce((s, r) => s + r, 0) / rets.length
  const variance = rets.reduce((s, r) => s + (r - mean) ** 2, 0) / (rets.length - 1)
  const sd = Math.sqrt(variance)
  if (sd === 0) return 0
  return (mean / sd) * Math.sqrt(periodsPerYear)
}

/**
 * Halve a full sample buffer by dropping every other entry, keeping the newest.
 *
 * A round runs for days at a few seconds per sample, which would overflow any
 * fixed buffer. Thinning trades resolution for reach: the curve keeps its whole
 * span and simply gets coarser the further back you look, which is exactly the
 * trade a reader would make.
 */
export function thin<T>(xs: T[]): T[] {
  const out: T[] = []
  for (let i = xs.length - 1; i >= 0; i -= 2) out.push(xs[i])
  return out.reverse()
}

export function maxDrawdownPct(equity: readonly number[]): number {
  let peak = -Infinity
  let worst = 0
  for (const e of equity) {
    if (e > peak) peak = e
    if (peak > 0) worst = Math.min(worst, (e / peak - 1) * 100)
  }
  return worst
}

